import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { splitIPAndPort, Feedback } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';

import { font } from '@styles/fonts';
import Button from '@styles/buttons';
import Svg from '@styles/icons/svg';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { useGetMachineConnectionMethods } from '@api/modules/machines/hooks';

const MachinesItem = ({ machines, status, index, machinesList, C, renderAction, mappedItem, style, connectionMethodName }) => {
  return (
    <View
      style={[
        {
          height: scale(72),
          width: '100%',
          flexDirection: 'row',
          paddingVertical: scale(7),
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: C('Hairline/Light/No trans'),
          backgroundColor: C(modalFieldBg),
        },
        index === 0 && {
          borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
        },
        index === machinesList.length - 1 && {
          borderBottomWidth: 0,
          borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
        },
        style,
      ]}
    >
      <View style={{ paddingLeft: scale(18) }}>
        <View style={{ width: scale(125) }}>
          <Text style={[{ color: C('Label/Light/Primary') }, font.Body, !machines.fullName && { textTransform: 'capitalize' }]} numberOfLines={1}>
            {machines.fullName || machines.machineBrandCode || connectionMethodName}{' '}
          </Text>
        </View>
        <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t(splitIPAndPort(machines.endpoint).ipAddress)}</Text>
        <View style={{ flexDirection: 'row' }}>
          <Text style={[{ color: C(status?.color) }, font.Footnote]} numberOfLines={1} ellipsizeMode={'middle'}>
            {i18n.t(status?.text)}
            {status?.ncFile && <Text style={[{ color: C('Label/Light/Quaternary') }, font['Caption 2']]}>{status?.ncFile}</Text>}
          </Text>
        </View>
      </View>
      <View
        style={{
          position: 'absolute',
          top: scale(machines.isDemo ? 30 : 13),
          right: scale(machines.isDemo ? 16 : 9),
        }}
      >
        {renderAction(mappedItem)}
      </View>
    </View>
  );
};

const MachinesList = ({
  machinesList,
  setMode,
  MODES,
  startMachineOnboardingSession,
  validateMachineOnboardingEndpoint,
  setData,
  validateUserAction,
  loadedMachineAddressesError,
  C,
}) => {
  const [loading, setLoading] = useState(false);
  const [localStatusStorage, setLocalStatusStorage] = useState({});

  const { machineConnectionMethods } = useGetMachineConnectionMethods();

  const handleReachable = async (item) => {
    if (loading) {
      return;
    }
    setLoading(item.endpoint);
    const machineOnboardingSessionId = await startMachineOnboardingSession(item);
    if (machineOnboardingSessionId) {
      const resp = (await validateMachineOnboardingEndpoint({ machineOnboardingSessionId })) || {};

      const isSuccess = resp?.endpointReachableResult?.isReachable;
      if (isSuccess) {
        const machine = { ...item, machineOnboardingSessionId };
        setData(machine);
        return validateUserAction(machine);
      }
    }

    setLocalStatusStorage({
      ...localStatusStorage,
      [item.endpoint]: {
        isReachable: false,
      },
    });

    setLoading(false);
  };

  const renderAction = (item) => {
    if (item.endpoint == loading) {
      return <SmallLoading />;
    }
    if (item.isDemo) {
      return <Svg icon="chevronRight" width={scale(7)} height={scale(11)} color={'Special/Light/Chevron'} />;
    }
    if (item.isReachable) {
      return (
        <Button
          {...{
            disabled: loading,
            variant: 'S',
            type: 'Secondary',
            onPress: () => handleReachable(item),
          }}
        >
          {i18n.t('Connect')}
        </Button>
      );
    }
  };

  const getMachineStatus = (machine) => {
    if (!machine.isReachable) {
      return { text: 'Connecting to the machine failed because of a timeout.', color: 'Label/Light/Error' };
    }
    if (machine.loading) {
      return { text: 'Connecting...', color: 'Label/Light/Primary' };
    }
    return { text: 'Online', color: 'System/Light/Green' };
  };

  const getConnectionMethodName = (machine) => machineConnectionMethods?.find((method) => method?.code === machine?.connectionMethodCode)?.name || '';

  return (
    <View style={{ paddingHorizontal: scale(15), marginTop: scale(7) }}>
      {loadedMachineAddressesError ? (
        <View style={{ paddingHorizontal: scale(15), paddingVertical: scale(50) }}>
          <Text style={[{ color: C('Label/Light/Tertiary'), width: '100%', textAlign: 'center' }, font.Footnote]}>
            {i18n.t('Problem with receiving machine list')}
          </Text>
        </View>
      ) : (
        machinesList.map((machines, index) => {
          const localStatus = localStatusStorage[machines.endpoint] || {};
          const mappedItem = {
            ...machines,
            ...localStatus,
          };
          const status = getMachineStatus(mappedItem);
          const connectionMethodName = getConnectionMethodName(mappedItem);

          const machinesItemProps = { C, index, machines, machinesList, mappedItem, renderAction, status };

          if (mappedItem.isDemo) {
            return (
              <TouchableElement
                onPress={() => {
                  setMode(MODES.DEMO_MACHINES);
                }}
                key={machines.endpoint}
              >
                {({ boxStyle, pressHoverColor }) => {
                  return (
                    <View>
                      <MachinesItem style={{ ...boxStyle, backgroundColor: C(pressHoverColor(modalFieldBg)) }} {...machinesItemProps} />
                    </View>
                  );
                }}
              </TouchableElement>
            );
          }

          return <MachinesItem key={machines.endpoint} {...machinesItemProps} connectionMethodName={connectionMethodName} />;
        })
      )}
    </View>
  );
};

export default ({
  C,
  hide,
  machines: { machineAddresses, loadedMachineAddresses, loadedMachineAddressesError },
  setData,
  setMode,
  MODES,
  maxHeight,
  validateUserAction,
  validateMachineOnboardingEndpoint,
  startMachineOnboardingSession,
}) => {
  const headerHeight = scale(58);

  const machinesList = [
    ...machineAddresses,
    { machineBrandCode: 'Demo Machines', endpoint: 'Virtual machines for demo & testing', isReachable: true, isDemo: true },
  ];

  const noMachinesFound = loadedMachineAddresses && !machinesList.length;

  return (
    <View style={{}}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: headerHeight,
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement onPress={() => hide()}>
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Add Machines')}</Text>
        </View>
      </View>
      <View style={{ paddingTop: scale(20), maxHeight: maxHeight - headerHeight }}>
        <View style={{ paddingHorizontal: scale(30), flexDirection: 'row' }}>
          <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>
            {i18n.t(noMachinesFound ? 'NO MACHINES FOUND' : 'SEARCHING FOR MACHINES')}
          </Text>
          {!loadedMachineAddresses && !loadedMachineAddressesError && (
            <View style={{ marginLeft: scale(7) }}>
              <SmallLoading />
            </View>
          )}
        </View>
        <ScrollView contentContainerStyle={{ paddingBottom: scale(18) }}>
          <MachinesList
            {...{
              C,
              loadedMachineAddressesError,
              machinesList,
              MODES,
              setData,
              setMode,
              startMachineOnboardingSession,
              validateMachineOnboardingEndpoint,
              validateUserAction,
            }}
          />
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(20) }}>
            <TouchableElement
              onPress={() => {
                setData({});
                setMode(MODES.ADD_MACHINE_IP);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      height: scale(42),
                      width: '100%',
                      paddingHorizontal: scale(15),
                      justifyContent: 'center',
                      backgroundColor: C(pressHoverColor(modalFieldBg)),
                      borderRadius: scale(SCREEN_BORDER_RADIUS),
                    },
                  ]}
                >
                  <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>{i18n.t('Add manually')}...</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(20) }}>
            <TouchableElement
              onPress={() => {
                setData({});
                setMode(MODES.SEARCH_SETTINGS);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      height: scale(42),
                      width: '100%',
                      paddingHorizontal: scale(15),
                      justifyContent: 'center',
                      backgroundColor: C(pressHoverColor(modalFieldBg)),
                      borderRadius: scale(SCREEN_BORDER_RADIUS),
                    },
                  ]}
                >
                  <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>{i18n.t('Search Settings')}...</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          {noMachinesFound && (
            <View style={{ paddingHorizontal: scale(15), marginTop: scale(20) }}>
              <TouchableElement
                onPress={() => {
                  Feedback({ type: 'help' });
                }}
              >
                {({ boxStyle, pressHoverColor }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        height: scale(42),
                        width: '100%',
                        paddingHorizontal: scale(15),
                        justifyContent: 'center',
                        backgroundColor: C(pressHoverColor(modalFieldBg)),
                        borderRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                    ]}
                  >
                    <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>{i18n.t('Ask For Help')}...</Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
};
